import { installmentAmount } from "../../../components/paiement/financial";
import React, {Fragment, useEffect, useMemo, useRef, useState} from "react";
import {isStockable} from "../stockable";
import {useRouter} from "next/router";
import {ConfigProvider, Form, Input} from "antd";
import {CreditCard, Handshake} from "lucide-react";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {ASSET_URL} from "../../../config";
import {applyLang, getDeviseName, getDeviseSymbol} from "../../../i18n";
import {getLayoutDirection} from "../../../locales";
import {getPurOrg, uuidv4} from "../../../utils";
import ApiService from "../../../services/ApiService";
import {navigateToPaiement, preparePaiement} from "../../../services/paiementLauncher";
import PhoneInput from "../../../components/WebEditor/components/PhoneInput";
import {getCampagneCurrencyOptions, getProductPrice, readCampagneDevise, storeCampagneDevise} from "../components/devise-choice";
import {afterCloseLink, countryName, defaultSplit, givingModel, isMonthlyDonation, MAX_PRODUCT_QUANTITY, realCost, resumeChoice, resumeCurrency, resumeProduct, sortedTiers, stockOf, targetWithTeam, taxBenefit, visibleTiers} from "./model";
import {showcaseChoice} from "./sections";
import styles from "./v2.module.css";

export const THANKS_STORAGE_PREFIX = "campagne_v2_merci_"

// Médailles de la maquette, repères visuels des paliers quand un montant n'a pas d'image.
const MEDALS = ["etoile", "flambeau", "argent", "or", "pilier", "or"]
const medalOf = (index) => "/campagne/v2/medaille-" + MEDALS[Math.min(index, MEDALS.length - 1)] + ".webp"

function parseAmount(raw) {
    if (raw === null || raw === undefined) return 0
    const n = Number(String(raw).replace(/\s/g, "").replace(",", "."))
    return Number.isFinite(n) ? n : 0
}

// Adresse de retour du prestataire, qui s'en sert aussi en cas d'annulation ou de refus :
// elle ne dit donc jamais que le don a abouti (cf. ThankYou), et ne garde pas le lien
// personnel d'un ambassadeur (amb).
export function returnUrlOf() {
    try {
        const url = new URL(window.location.href)
        url.searchParams.delete("pc")
        url.searchParams.delete("amb")
        url.searchParams.delete("merci")
        // Comme buildReturnUrl : le fragment avalerait les paramètres de succès Stripe.
        url.hash = ""
        return url.toString()
    } catch (e) {
        return window.location.href.split("#")[0]
    }
}

/**
 * Bloc de don de la page v2 : un geste, un don.
 * Le donateur touche un montant (ou saisit le sien), le bouton affiche la somme, il
 * valide. Quantité, fréquence, équipe, devise et promesse n'apparaissent que si la
 * campagne les propose. L'envoi reprend exactement le contrat de la fenêtre de don
 * historique (preparePaiement, réservation des lots, promesse publique).
 */
export default function DonationPanel({context, campagneData, campagneTarget, life, goal, palette, formatMoney, onDonated, onCheckout, onPromiseStateChange, requestedTier, preview = false}) {
    const router = useRouter()
    const config = campagneData?.config || {}
    const {org} = context

    const tiers = useMemo(
        () => sortedTiers(campagneData?.products || []).map(p => applyLang({...p}, context.lang)),
        [campagneData?.products, context.lang]
    )

    // Façon de donner (don unique, étalable, mensuel, au choix) : cf. givingModel.
    const stockable = isStockable(campagneData)
    const giving = givingModel(config, {stockable})
    const withQuantity = stockable || goal.mode === "UNITS"
    const freeAmountAllowed = !stockable && config.fixedMontant !== true
    // Comme la fenêtre de don historique : l'étalement est proposé par défaut jusqu'à
    // 12 mois ; « 1 » le coupe.
    const maxSplit = giving.maxSplit
    const promiseAvailable = campagneData?.gala === true && config.noPromesse !== true
    const visibleTeams = (campagneData?.teams || []).filter(team => !team.hidden)
    const askTeam = visibleTeams.length > 0 && config.modalHideTeam !== true

    const currencyOptions = getCampagneCurrencyOptions(context, campagneData)
    const principalDevise = currencyOptions[0].devise
    const requestedDevise = context.query?.devise
    const resumeOption = resumeCurrency(requestedDevise, currencyOptions)
    const resumeCurrencyUnavailable = !!requestedDevise && !resumeOption
    const initialCurrency = resumeOption || currencyOptions[0]
    const [devise, setDevise] = useState(initialCurrency.devise)
    const currentCurrency = currencyOptions.find(c => c.devise === devise) || currencyOptions[0]
    const rate = currentCurrency.type === "PRINCIPAL" ? 1 : (Number(currentCurrency.rate) || 1)

    const firstAvailableOf = (list) => list.find(p => stockOf(campagneData, p.id) !== 0)?.id || null
    const defaultTier = config.noDefaultProduct === true ? null : firstAvailableOf(visibleTiers(giving, tiers, "ONCE"))
    const hasResumeAmount = context.query?.don !== undefined
    const productResume = stockable && !resumeCurrencyUnavailable && context.query?.lot !== undefined
        ? resumeProduct(tiers, context.query?.lot, context.query?.quantite,
            p => stockOf(campagneData, p.id), context.query?.don,
            p => getProductPrice(p, initialCurrency.devise, principalDevise, initialCurrency.rate, initialCurrency.type))
        : undefined

    // Lien « reprendre mon don » : le montant de la tentative précédente est pré-choisi.
    const resume = useMemo(() => !stockable && !resumeCurrencyUnavailable && productResume === undefined && resumeChoice(
        tiers.filter(p => stockOf(campagneData, p.id) !== 0),
        context.query?.don,
        {freeAmountAllowed, priceOf: p => getProductPrice(p, initialCurrency.devise, principalDevise, initialCurrency.rate, initialCurrency.type)},
    ), []) // eslint-disable-line react-hooks/exhaustive-deps -- lu une fois, à l'arrivée sur la page

    const [tierId, setTierId] = useState(resumeCurrencyUnavailable || (stockable && hasResumeAmount && !productResume)
        ? null : productResume !== undefined ? productResume?.tierId || null : resume ? resume.tierId : defaultTier)
    // « Au choix du donateur » : une fois ou chaque mois.
    const [choice, setChoice] = useState("ONCE")
    const [freeAmount, setFreeAmount] = useState(resume ? resume.freeAmount : "")
    const [quantity, setQuantity] = useState(productResume?.quantity || 1)
    const [split, setSplit] = useState(0)
    const [nbMonth, setNbMonth] = useState(() => {
        if (config.forceMonthNb !== undefined && config.forceMonthNb !== null) return Number(config.forceMonthNb) || 0
        return Number(config.defaultNbMonth) > 1 ? Number(config.defaultNbMonth) : 0
    })
    const [team, setTeam] = useState(campagneTarget?.team || "")
    const [mode, setMode] = useState("PAIEMENT")
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)
    const [promiseLink, setPromiseLink] = useState(null)
    const promiseKey = useRef(uuidv4())
    const freeInputRef = useRef(null)

    useEffect(() => {
        setTeam(campagneTarget?.team || "")
    }, [campagneTarget?.team])

    // Devise retenue pour cette campagne (même mémoire que l'ancien écran de don) : un
    // simple rechargement replaçait le donateur sur la devise principale, sans un mot.
    // Posée après le montage : la page est rendue côté serveur, où cette mémoire
    // n'existe pas.
    useEffect(() => {
        if (requestedDevise || hasResumeAmount) return
        const memorisee = readCampagneDevise(campagneData?.id)
        if (memorisee && memorisee !== devise && currencyOptions.some(c => c.devise === memorisee)) {
            setDevise(memorisee)
        }
    }, [campagneData?.id]) // eslint-disable-line react-hooks/exhaustive-deps -- lu une fois, à l'arrivée sur la page

    useEffect(() => {
        if (resumeOption) storeCampagneDevise(campagneData?.id, resumeOption.devise)
        else if (hasResumeAmount && !requestedDevise) storeCampagneDevise(campagneData?.id, principalDevise)
    }, [campagneData?.id, resumeOption?.devise]) // eslint-disable-line react-hooks/exhaustive-deps -- synchroniser la galerie avec le lien de reprise

    const tier = tiers.find(p => p.id === tierId) || null
    const shownTiers = visibleTiers(giving, tiers, choice)
    const monthly = tier
        ? isMonthlyDonation(giving, tier, choice)
        : (giving.mode === "MONTHLY" || (giving.mode === "CHOICE" && choice === "MONTHLY"))
    const stock = tier ? stockOf(campagneData, tier.id) : null
    // Les réservations suivent la limite du moteur commun. Les parrainages sans
    // stock restent libres comme dans main ; seul leur montant doit être fini.
    const maxQuantity = !stockable ? Infinity
        : stock !== null && stock >= 0 ? Math.min(MAX_PRODUCT_QUANTITY, stock) : MAX_PRODUCT_QUANTITY
    const unitPrice = tier ? getProductPrice(tier, devise, principalDevise, rate, currentCurrency.type) : 0
    const amount = tier ? unitPrice * quantity : parseAmount(freeAmount)

    // Un lot qui impose un étalement (product.divide) le fixe ; au-delà du seuil de la
    // campagne, l'étalement proposé par défaut est celui de la campagne.
    const forcedSplit = !stockable && tier?.divide ? Number(tier.divide) : 0
    // Proposé à partir d'un montant au moins égal au nombre de mois (1 par mois au
    // minimum), comme dans la fenêtre historique.
    const splitAllowed = !monthly && mode === "PAIEMENT" && (forcedSplit > 1 || (maxSplit > 1 && amount >= maxSplit))
    const splitOptions = splitAllowed ? Array.from({length: Math.max(maxSplit, forcedSplit) - 1}, (_, i) => i + 2) : []
    const effectiveSplit = forcedSplit > 1 ? forcedSplit : (splitAllowed ? split : 0)

    useEffect(() => {
        const threshold = Number(config.forceDivideGTEAmount) || 0
        const forcedCount = Number(config.forceDivideNB) || 0
        if (splitAllowed && threshold > 0 && forcedCount > 1 && forcedCount <= maxSplit && amount >= threshold) {
            setSplit(current => current || forcedCount)
        }
    }, [amount])

    // Le stock public inclut notre propre réservation pendant le checkout. Une
    // actualisation ne doit donc jamais réécrire le choix du donateur, même après
    // « Modifier mon don ». L'API vérifie le stock à chaque nouvelle réservation.

    const minMontant = Number(config.minMontant) > 0 ? Number(config.minMontant) : 1

    const splitPerMonth = effectiveSplit > 1 ? installmentAmount(amount, effectiveSplit) : 0

    const benefit = taxBenefit(
        currentCurrency.type === "REAL" ? currentCurrency.country : campagneData?.mainAssocInfo?.country,
        config
    )
    const cost = realCost(effectiveSplit > 1 ? splitPerMonth : amount, benefit)
    const costText = cost !== null ? formatMoney(cost, devise) : ""
    const perMonth = monthly || effectiveSplit > 1

    const issuer = currentCurrency.type === "REAL"
        ? {assocLabel: currentCurrency.assocLabel, country: currentCurrency.country, charityNumber: currentCurrency.charityNumber}
        : campagneData?.mainAssocInfo

    // Le montant libre suit le rythme de la campagne, ou la bascule du donateur.
    const freeMonthly = giving.mode === "MONTHLY" || (giving.mode === "CHOICE" && choice === "MONTHLY")

    const switchChoice = (next) => {
        if (next === choice) return
        setChoice(next)
        setTierId(config.noDefaultProduct === true ? null : firstAvailableOf(visibleTiers(giving, tiers, next)))
        setQuantity(1)
        setFreeAmount("")
        setSplit(0)
        setError(null)
    }

    const selectTier = (id) => {
        setTierId(id)
        setQuantity(1)
        setFreeAmount("")
        // Un étalement choisi pour le montant précédent ne suit pas le nouveau : le
        // donateur se retrouvait avec « 240 € par mois pendant 5 mois » sans l'avoir
        // demandé, comme le fait déjà le changement de rythme.
        setSplit(0)
        setError(null)
    }

    useEffect(() => {
        if (!requestedTier?.id) return
        const product = tiers.find(item => item.id === requestedTier.id)
        if (!product || stockOf(campagneData, product.id) === 0) return
        // La galerie affiche les montants sans fréquence propre comme des dons uniques.
        // Aligner la bascule du formulaire, même si le visiteur était sur « Chaque mois ».
        if (giving.mode === "CHOICE") setChoice(showcaseChoice(giving, product))
        selectTier(product.id)
    }, [requestedTier]) // eslint-disable-line react-hooks/exhaustive-deps -- chaque clic dans la liste est une nouvelle demande

    const onFreeAmount = (value) => {
        // Le champ n'accepte que des chiffres. Retirer « - » ou « e » sans rien dire
        // transformait « -5 » en 5 et « 1e10 » en 110, des montants que le donateur
        // n'a jamais tapés.
        const propre = value.replace(/[^\d.,\s]/g, "")
        setFreeAmount(propre)
        if (value) setTierId(null)
        setError(propre === value ? null : t`Ce champ n'accepte que des chiffres.`)
    }

    const buttonLabel = () => {
        if (!(amount > 0)) return t`Choisissez un montant`
        if (mode === "PROMESSE") return t`Je promets ${formatMoney(amount, devise)}`
        if (effectiveSplit > 1) {
            return giving.wording === "MONTHLY"
                ? t`Donner ${formatMoney(splitPerMonth, devise)} par mois pendant ${effectiveSplit} mois`
                : t`Donner ${formatMoney(amount, devise)} en ${effectiveSplit} fois`
        }
        if (monthly) return t`Donner ${formatMoney(amount, devise)} par mois`
        return t`Donner ${formatMoney(amount, devise)}`
    }

    // Même conversion que la fenêtre historique : devise principale ou virtuelle →
    // montant reconverti en devise principale ; devise réelle → conservée.
    const resolveAmount = (value) => {
        if (currentCurrency.type === "REAL") return {montant: value, devise}
        const principalAmount = rate !== 1 ? Math.round((value / rate) * 100) / 100 : value
        return {montant: principalAmount, devise: principalDevise}
    }
    const charged = resolveAmount(amount)
    // Convert the selected total before splitting, exactly as the shared checkout
    // does. A virtual display currency is never the bank's debit currency.
    const chargedPerMonth = effectiveSplit > 1 ? installmentAmount(charged.montant, effectiveSplit) : charged.montant

    const buildCampagne = () => {
        const campagne = {
            ...targetWithTeam(campagneTarget, team),
            theme: palette.accent,
            label: campagneData.label || "",
            // Devise réellement encaissée : l'association qui encaisse est celle déclarée
            // pour cette devise. C'est elle qui émet le reçu, ouvre droit au Gift Aid et
            // décide de l'avantage fiscal affiché — l'API retient de toute façon la même.
            assoc: currentCurrency.type === "REAL" && currentCurrency.assocId
                ? currentCurrency.assocId
                : (campagneData.assoc || "MAIN"),
            id: campagneData.id,
        }
        if (currentCurrency.type === "VIRTUAL") {
            campagne.donorDevise = devise
            campagne.donorAmount = amount
        }
        if (stockable && tier) {
            campagne.products = [{id: tier.id, quantity}]
        }
        return campagne
    }

    const validate = () => {
        if (!Number.isFinite(amount) || !(amount > 0)) {
            setError(t`Choisissez un montant ou saisissez le vôtre.`)
            return false
        }
        if (!tier && amount < minMontant) {
            setError(t`Le montant minimum est de ${formatMoney(minMontant, devise)}.`)
            return false
        }
        if (stockable && !tier) {
            setError(t`Choisissez un lot.`)
            return false
        }
        return true
    }

    const submitPayment = async () => {
        // Aperçu depuis le back-office : on montre la page, on ne prend pas de don.
        if (preview) {
            setError(t`Aperçu : le don n'est pas envoyé.`)
            return
        }
        if (!validate()) return
        setSubmitting(true)
        try {
            const campagne = buildCampagne()
            if (stockable) campagne.lockId = uuidv4()

            const object = {
                montant: amount,
                divide: effectiveSplit > 1 ? effectiveSplit : 0,
                // La page de paiement commune ne présente le don en mensualités que si
                // l'étalement est possible : sur une campagne « don unique » avec un
                // montant qui impose son propre étalement, elle annonçait un paiement
                // unique alors qu'un abonnement allait être créé.
                maxDivide: Math.max(Number(config.maxDivideNB) || 0, effectiveSplit > 1 ? effectiveSplit : 0) || config.maxDivideNB,
                returnUrl: returnUrlOf(),
                organisation: getPurOrg(org),
                type: "CAMPAGNE",
                typeId: campagneData.id,
                cerfa: true,
                campagne,
                fixedProducts: stockable,
                selectedProductId: tier?.id || null,
                monthlyAmountDisplay: !monthly && config.monthlyAmountDisplay === true,
            }

            if (monthly) {
                object.maxNbMonth = config.maxNbMonth || 12
                object.minNbMonth = config.minNbMonth || null
                object.nbMonth = nbMonth
                if (config.forceMonthNb !== undefined && config.forceMonthNb !== null) {
                    object.fixedNbMonth = true
                }
            }

            const resolved = resolveAmount(object.montant)
            object.montant = resolved.montant
            object.devise = resolved.devise

            try {
                window.sessionStorage.setItem(THANKS_STORAGE_PREFIX + campagneData.id, JSON.stringify({
                    amount, devise, split: effectiveSplit, monthly, team: team || null, at: Date.now(),
                }))
            } catch (e) { /* stockage indisponible : pas d'écran de remerciement personnalisé */ }

            const paiementUrl = onCheckout ? null : preparePaiement(object, {org, query: "theme=CAMPAGNE"})

            if (stockable) {
                const res = await ApiService.post("/campagne/lock/", {
                    id: campagne.lockId,
                    campagne: campagneData.id,
                    data: {products: campagne.products},
                })
                if (res && res.success === false) {
                    setError(res.error === "STOCK_UNAVAILABLE"
                        ? t`Ce lot vient d'être réservé par quelqu'un d'autre. Choisissez-en un autre.`
                        : t`La réservation n'a pas pu être faite, veuillez réessayer.`)
                    return
                }
            }

            onDonated?.()

            // Écran de don de la campagne : on reste sur la page, avec le même contrat
            // de données que la page de paiement commune (cf. checkout/payload.js).
            if (onCheckout) {
                onCheckout({
                    amount: object.montant,
                    devise: object.devise,
                    months: monthly ? nbMonth : (effectiveSplit > 1 ? effectiveSplit : 0),
                    monthly,
                    selectedProductId: tier?.id || null,
                    campagne,
                    returnUrl: object.returnUrl,
                })
                return
            }

            navigateToPaiement(paiementUrl, router)
        } catch (e) {
            setError(t`Une erreur est survenue, veuillez réessayer.`)
        } finally {
            setSubmitting(false)
        }
    }

    const submitPromise = async (values) => {
        if (preview) {
            setError(t`Aperçu : la promesse n'est pas enregistrée.`)
            return
        }
        if (!validate()) return
        setSubmitting(true)
        try {
            const campagne = buildCampagne()
            if (values.displayName) campagne.displayName = values.displayName
            const resolved = resolveAmount(amount)
            const res = await ApiService.post("/campagne/promesse/" + campagneData.id, {
                montant: resolved.montant,
                devise: resolved.devise,
                idempotencyKey: promiseKey.current,
                campagne,
                infos: {nom: values.nom, prenom: values.prenom, email: values.email, tel: values.tel},
            })
            if (res && res.success) {
                setPromiseLink(res.url)
                onPromiseStateChange?.(true)
            } else {
                setError(t`Une erreur est survenue, veuillez réessayer.`)
            }
        } catch (e) {
            setError(t`Une erreur est survenue, veuillez réessayer.`)
        } finally {
            setSubmitting(false)
        }
    }

    // ---------- Dons fermés ----------

    if (!life.donationsOpen) {
        const dateFormat = new Intl.DateTimeFormat(context.langValue || "fr", {day: "numeric", month: "long", year: "numeric"})
        const orgName = org?.name || ""
        return (
            <section className={styles.donate} aria-labelledby="cv-donate-title">
                <h2 id="cv-donate-title" className={styles.srOnly}><Trans>Faire un don</Trans></h2>
                <div className={styles.notice} role="status">
                    {life.notStarted && life.start ? (
                        <Fragment>
                            <strong><Trans>Les dons ne sont pas encore ouverts</Trans></strong>
                            <Trans>Revenez le {dateFormat.format(life.start)} pour participer.</Trans>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <strong><Trans>Cette campagne est terminée</Trans></strong>
                            <Trans>Merci à toutes celles et ceux qui y ont participé.</Trans>
                            {/* La collecte est close, l'envie de donner ne l'est pas : on renvoie
                                poliment vers le don général de l'organisation. */}
                            <div><Trans>Vous pouvez toujours soutenir {orgName}</Trans></div>
                            <a className={styles.secondary + " " + styles.noticeCta} href={afterCloseLink(campagneData, org)}>
                                <Trans>Faire un don</Trans>
                            </a>
                        </Fragment>
                    )}
                </div>
            </section>
        )
    }

    // ---------- Promesse enregistrée ----------

    if (promiseLink) {
        return (
            <section className={styles.donate} aria-labelledby="cv-donate-title">
                <h2 id="cv-donate-title" className={styles.donateTitle}><Trans>Merci pour votre promesse !</Trans></h2>
                <div className={styles.notice} role="status">
                    <Trans>Vous pourrez la régler plus tard grâce à ce lien, également envoyé par SMS :</Trans>{" "}
                    <a href={promiseLink} target="_blank" rel="noopener noreferrer">{promiseLink}</a>
                </div>
                <button type="button" className={styles.secondary} onClick={() => {
                    promiseKey.current = uuidv4()
                    setPromiseLink(null)
                    setMode("PAIEMENT")
                    onPromiseStateChange?.(false)
                }}>
                    <Trans>Faire un autre don</Trans>
                </button>
            </section>
        )
    }

    const unitName = (n) => (n > 1 ? goal.unitLabel?.other : goal.unitLabel?.one) || null

    return (
        <section id="cv-donate" className={styles.donate} aria-labelledby="cv-donate-title">
            <h2 id="cv-donate-title" className={styles.donateTitle} tabIndex={-1}>
                {config.donationButtonText || <Trans>Je participe</Trans>}
            </h2>

            {resumeCurrencyUnavailable && hasResumeAmount && (
                <p className={styles.notice} role="status">
                    <Trans>La devise de votre précédent don n'est plus disponible. Choisissez une devise et un montant pour continuer.</Trans>
                </p>
            )}

            {promiseAvailable && (
                <fieldset className={styles.donationModes}>
                    <legend className={styles.donationModesLegend}><Trans>Comment souhaitez-vous contribuer ?</Trans></legend>
                    <div className={styles.donationModeCards}>
                        <label className={styles.donationModeCard} data-selected={mode === "PAIEMENT"}>
                            <span className={styles.donationModeCardTop}>
                                <CreditCard size={20} strokeWidth={1.75} aria-hidden="true" />
                                <input type="radio" name="cv-donation-mode" value="PAIEMENT" checked={mode === "PAIEMENT"}
                                    aria-labelledby="cv-mode-payment-title" aria-describedby="cv-mode-payment-help"
                                    onChange={() => setMode("PAIEMENT")} />
                            </span>
                            <span className={styles.donationModeCardText}>
                                <span id="cv-mode-payment-title" className={styles.donationModeCardTitle}><Trans>Faire un don</Trans></span>
                                <span id="cv-mode-payment-help" className={styles.donationModeCardHelp}><Trans>Je passe au paiement en ligne.</Trans></span>
                            </span>
                        </label>
                        <label className={styles.donationModeCard} data-selected={mode === "PROMESSE"}>
                            <span className={styles.donationModeCardTop}>
                                <Handshake size={20} strokeWidth={1.75} aria-hidden="true" />
                                <input type="radio" name="cv-donation-mode" value="PROMESSE" checked={mode === "PROMESSE"}
                                    aria-labelledby="cv-mode-pledge-title" aria-describedby="cv-mode-pledge-help"
                                    onChange={() => { setMode("PROMESSE"); switchChoice("ONCE") }} />
                            </span>
                            <span className={styles.donationModeCardText}>
                                <span id="cv-mode-pledge-title" className={styles.donationModeCardTitle}><Trans>Promesse de don</Trans></span>
                                <span id="cv-mode-pledge-help" className={styles.donationModeCardHelp}><Trans>Je m’engage sans payer à cette étape.</Trans></span>
                            </span>
                        </label>
                    </div>
                </fieldset>
            )}

            {/* Le code ISO seul ne dit rien à un donateur : le sélecteur montre le symbole
                et le nom de la devise dans sa langue. */}
            {currencyOptions.length > 1 && (
                <div className={styles.segment} role="group" aria-label={t`Devise du don`}>
                    {currencyOptions.map(c => (
                        <button
                            key={c.devise}
                            type="button"
                            className={styles.segmentButton}
                            aria-pressed={devise === c.devise}
                            onClick={() => {
                                setDevise(c.devise)
                                storeCampagneDevise(campagneData?.id, c.devise)
                            }}
                            title={c.label || getDeviseName(context, c.devise) || c.devise.toUpperCase()}
                        >
                            <span aria-hidden="true">{getDeviseSymbol(context, c.devise)}</span>
                            <span className={styles.segmentButtonLabel}>
                                {c.label || getDeviseName(context, c.devise) || c.devise.toUpperCase()}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {giving.mode === "CHOICE" && mode === "PAIEMENT" && (
                <div className={styles.segment} role="group" aria-label={t`Fréquence du don`}>
                    <button type="button" className={styles.segmentButton} aria-pressed={choice === "ONCE"} onClick={() => switchChoice("ONCE")}>
                        <Trans>Une fois</Trans>
                    </button>
                    <button type="button" className={styles.segmentButton} aria-pressed={choice === "MONTHLY"} onClick={() => switchChoice("MONTHLY")}>
                        <Trans>Chaque mois</Trans>
                    </button>
                </div>
            )}

            {shownTiers.length > 0 && (
                <div className={styles.tiers} role="group" aria-labelledby="cv-donate-title">
                    {shownTiers.map((p, index) => {
                        const selected = p.id === tierId
                        const tierStock = stockOf(campagneData, p.id)
                        const soldOut = tierStock === 0
                        return (
                            <button
                                key={p.id}
                                type="button"
                                aria-pressed={selected}
                                disabled={soldOut}
                                className={styles.tier}
                                style={{
                                    background: palette.tints[index] || palette.tints[palette.tints.length - 1],
                                    border: "1px solid " + (palette.borders[index] || palette.borders[palette.borders.length - 1]),
                                }}
                                onClick={() => selectTier(p.id)}
                            >
                                <img src={p.image ? ASSET_URL + p.image : medalOf(index)} alt="" className={styles.tierImage} loading="lazy"/>
                                <span className={styles.tierPrice}>
                                    {formatMoney(getProductPrice(p, devise, principalDevise, rate, currentCurrency.type), devise)}
                                    {isMonthlyDonation(giving, p, choice) && <span className={styles.tierPer}> <Trans>/mois</Trans></span>}
                                </span>
                                {p.name && <span className={styles.tierLabel} dir="auto">{p.name}</span>}
                                {p.description && <span className={styles.tierDescription} dir="auto">{p.description}</span>}
                                {soldOut && <span className={styles.tierStock}><Trans>Épuisé</Trans></span>}
                                {!soldOut && tierStock !== null && tierStock > 0 && tierStock <= 10 && (
                                    <span className={styles.tierStock}><Trans>Plus que {tierStock}</Trans></span>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}

            {tier && withQuantity && (maxQuantity > 1 || quantity > 1) && (
                <div className={styles.quantity}>
                    <span className={styles.quantityLabel} id="cv-quantity-label">
                        {goal.mode === "UNITS" && unitName(quantity) ? unitName(quantity) : <Trans>Quantité</Trans>}
                    </span>
                    <div className={styles.stepper} role="group" aria-labelledby="cv-quantity-label">
                        <button type="button" className={styles.stepperButton} aria-label={t`Retirer un`} disabled={quantity <= 1} onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                        <span className={styles.stepperValue} aria-live="polite">× {quantity}</span>
                        <button type="button" className={styles.stepperButton} aria-label={t`Ajouter un`} disabled={quantity >= maxQuantity} onClick={() => setQuantity(q => Math.min(maxQuantity, q + 1))}>+</button>
                    </div>
                </div>
            )}

            {freeAmountAllowed && (
                <div className={styles.field}>
                    <label className={styles.srOnly} htmlFor="cv-free-amount">
                        {freeMonthly ? t`Autre montant par mois` : t`Autre montant`}
                    </label>
                    <div className={styles.amountRow}>
                        <input
                            ref={freeInputRef}
                            id="cv-free-amount"
                            className={styles.input}
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder={freeMonthly ? t`Autre montant par mois` : t`Autre montant`}
                            value={freeAmount}
                            aria-invalid={!!error && !tier}
                            aria-describedby={error ? "cv-donate-error" : undefined}
                            onChange={(e) => onFreeAmount(e.target.value)}
                        />
                        <span className={styles.currencySymbol} aria-hidden="true">{getDeviseSymbol(context, devise)}</span>
                    </div>
                </div>
            )}

            {splitOptions.length > 0 && forcedSplit <= 1 && (
                <div className={styles.field}>
                    <div className={styles.segment} role="group" aria-label={t`Fréquence du don`}>
                        <button type="button" className={styles.segmentButton} aria-pressed={split === 0} onClick={() => setSplit(0)}>
                            <Trans>En une fois</Trans>
                        </button>
                        <button type="button" className={styles.segmentButton} aria-pressed={split > 1} onClick={() => setSplit(s => s || defaultSplit(amount, splitOptions))}>
                            <Trans>En plusieurs fois</Trans>
                        </button>
                    </div>
                    {split > 1 && (
                        <Fragment>
                            <label className={styles.srOnly} htmlFor="cv-split">{t`Nombre de mensualités`}</label>
                            <select id="cv-split" className={styles.select} value={split} onChange={(e) => setSplit(Number(e.target.value))}>
                                {splitOptions.filter(n => amount / n >= 1).map(n => (
                                    <option key={n} value={n}>
                                        {giving.wording === "MONTHLY"
                                            ? t`${formatMoney(Math.ceil(amount / n), devise)} par mois pendant ${n} mois`
                                            : t`Diviser mon don sur ${n} mois`}
                                    </option>
                                ))}
                            </select>
                        </Fragment>
                    )}
                </div>
            )}

            {monthly && mode === "PAIEMENT" && giving.duration !== "CHOICE" && (
                <p className={styles.help}>
                    {giving.duration === "FIXED"
                        ? t`Pendant ${giving.fixedMonths} mois`
                        : t`Chaque mois, sans date de fin (résiliable à tout moment)`}
                </p>
            )}

            {monthly && mode === "PAIEMENT" && giving.duration === "CHOICE" && (
                <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="cv-months"><Trans>Durée</Trans></label>
                    <select
                        id="cv-months"
                        className={styles.select}
                        value={nbMonth}
                        onChange={(e) => setNbMonth(Number(e.target.value))}
                    >
                        <option value={0}>{t`Chaque mois, sans date de fin (résiliable à tout moment)`}</option>
                        {Array.from({length: Math.max(1, (Number(config.maxNbMonth) || 12) - 1)}, (_, i) => i + 2)
                            .filter(n => !(Number(config.minNbMonth) > n))
                            .reverse()
                            .map(n => <option key={n} value={n}>{t`Pendant ${n} mois`}</option>)}
                    </select>
                </div>
            )}

            {askTeam && (
                <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="cv-team"><Trans>Soutenir une équipe (facultatif)</Trans></label>
                    <select id="cv-team" className={styles.select} value={team} onChange={(e) => setTeam(e.target.value)}>
                        <option value="">{t`Aucune équipe en particulier`}</option>
                        {visibleTeams.map(item => <option key={item.param} value={item.param}>{item.label}</option>)}
                    </select>
                </div>
            )}

            {error && <p id="cv-donate-error" className={styles.error} role="alert">{error}</p>}

            {mode === "PROMESSE" ? (
                <ConfigProvider
                    direction={getLayoutDirection(context.langValue)}
                    theme={{token: {colorPrimary: palette.accent, borderRadius: 12, fontFamily: "inherit", controlHeight: 48, fontSize: 16}}}
                >
                    <Form layout="vertical" requiredMark={false} onFinish={submitPromise} style={{marginTop: 16}}>
                        <Form.Item label={t`Prénom`} name="prenom" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                            <Input autoComplete="given-name"/>
                        </Form.Item>
                        <Form.Item label={t`Nom`} name="nom" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                            <Input autoComplete="family-name"/>
                        </Form.Item>
                        <Form.Item label={t`Email`} name="email" rules={[{required: true, message: t`Ce champ est obligatoire`}, {type: "email", message: t`Cette adresse email n'est pas valide`}]}>
                            <Input type="email" autoComplete="email"/>
                        </Form.Item>
                        <Form.Item label={t`Téléphone`} name="tel" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                            <PhoneInput placeholder={t`Téléphone`} defaultLang={context.langValue}/>
                        </Form.Item>
                        <Form.Item label={t`Nom affiché sur la page (facultatif)`} name="displayName" extra={t`Laissez vide pour rester anonyme.`}>
                            <Input autoComplete="off"/>
                        </Form.Item>
                        <button type="submit" className={styles.primary} disabled={submitting}>
                            {buttonLabel()}
                        </button>
                    </Form>
                </ConfigProvider>
            ) : (
                <button type="button" className={styles.primary} disabled={submitting} onClick={submitPayment}>
                    {submitting ? t`Un instant…` : buttonLabel()}
                </button>
            )}

            {amount > 0 && (
                <div className={styles.receipt}>
                    {/* Comme sur la maquette : débit puis organisme émetteur, d'une traite. */}
                    <p className={styles.receiptLine}>
                        {mode === "PAIEMENT" && (
                            effectiveSplit > 1 ? (
                                <Trans>Vous serez débité de <strong>{formatMoney(chargedPerMonth, charged.devise)}</strong> par mois pendant {effectiveSplit} mois.</Trans>
                            ) : monthly ? (
                                nbMonth > 1
                                    ? <Trans>Vous serez débité de <strong>{formatMoney(charged.montant, charged.devise)}</strong> chaque mois, pendant {nbMonth} mois.</Trans>
                                    : <Trans>Vous serez débité de <strong>{formatMoney(charged.montant, charged.devise)}</strong> chaque mois.</Trans>
                            ) : (
                                <Trans>Vous serez débité de <strong>{formatMoney(charged.montant, charged.devise)}</strong>.</Trans>
                            )
                        )}
                        {mode === "PROMESSE" && (
                            <Trans>Rien n'est débité maintenant : vous recevrez un lien pour régler votre promesse.</Trans>
                        )}
                        {issuer?.assocLabel && (
                            <Fragment>
                                {" "}
                                <Trans>Reçu fiscal émis par <strong>{issuer.assocLabel}</strong></Trans>
                                {issuer.charityNumber ? <Fragment>{" · "}<Trans>N° d'association</Trans>{" " + issuer.charityNumber}</Fragment> : ""}
                                {issuer.country ? " · " + countryName(issuer.country, context.langValue) : ""}
                            </Fragment>
                        )}
                    </p>
                    {cost !== null && benefit?.kind === "REDUCTION" && (
                        <p className={styles.receiptLine}>
                            {perMonth
                                ? <Trans>Après réduction d'impôt de 66 %, ce don vous revient à <strong>{costText}</strong> par mois.</Trans>
                                : <Trans>Après réduction d'impôt de 66 %, ce don vous revient à <strong>{costText}</strong>.</Trans>}
                        </p>
                    )}
                    {cost !== null && benefit?.kind === "CREDIT" && (
                        <p className={styles.receiptLine}>
                            {perMonth
                                ? <Trans>Avec le crédit d'impôt de 35 % (article 46), ce don vous revient à <strong>{costText}</strong> par mois.</Trans>
                                : <Trans>Avec le crédit d'impôt de 35 % (article 46), ce don vous revient à <strong>{costText}</strong>.</Trans>}
                        </p>
                    )}
                    {benefit?.kind === "GIFT_AID" && (
                        <p className={styles.receiptLine}>
                            <Trans>Contribuable britannique ? Avec le Gift Aid, l'association reçoit 25 % de plus, sans frais pour vous.</Trans>
                        </p>
                    )}
                </div>
            )}
        </section>
    )
}
